import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs/operators';

import { RuleService } from '../../../services/rule.service';
import { RuleInfo } from '../../models/rule-config';
import { ProcessType } from '../../models/request';
import { getToolByServerProcessType } from '../../models/jit-tool-registry.helpers';
import { CreateRuleDialogComponent } from '../process/create-rule-dialog/create-rule-dialog.component';

/**
 * The "Rules" tab (jit-ui#133, Phase 2 of the bucket-monitoring auto-ingest
 * feature - doc/JIT-Rules.md Frontend Design). Lists the caller's Rules (or
 * every rule visible on buckets they can access, via the mine/all toggle),
 * with toolbar-level (not per-row) Edit/Activate/Deactivate/Delete actions
 * acting on the currently selected row. Rules are created from a tool page
 * (see ProcessComponentBase.createRule / CreateRuleDialogComponent) - there
 * is no "New Rule" action here, only Edit for an existing one (jit-ui#137).
 */
@Component({
  selector: 'rules',
  templateUrl: './rules.component.html',
  styleUrls: ['./rules.component.scss'],
})
export class RulesComponent implements OnInit {

  rules: RuleInfo[] = [];
  loading = false;
  scope: 'mine' | 'all' = 'mine';
  selectedRule: RuleInfo | null = null;

  constructor(
    private ruleService: RuleService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  toolName(rule: RuleInfo): string {
    return getToolByServerProcessType(rule.processType as ProcessType)?.displayName ?? rule.processType;
  }

  monitoredDirectory(rule: RuleInfo): string {
    return rule.pathPrefix ? `${rule.bucket}/${rule.pathPrefix}` : rule.bucket;
  }

  setScope(scope: 'mine' | 'all'): void {
    if (this.scope === scope) return;
    this.scope = scope;
    this.selectedRule = null;
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    const selectedId = this.selectedRule?.id;
    this.ruleService.list(this.scope).subscribe({
      next: (rules) => {
        this.rules = rules;
        // Rebind to the same row's fresh data (e.g. after activate/deactivate)
        // rather than leaving `selectedRule` pointing at the stale object -
        // otherwise the toolbar keeps evaluating stale fields like `enabled`.
        // Clear it if the row is no longer in the list (e.g. deleted elsewhere).
        this.selectedRule = selectedId != null ? rules.find((r) => r.id === selectedId) ?? null : null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast('error', 'Could Not Load Rules', 'Failed to load rules.');
      },
    });
  }

  /**
   * Opens the same create/edit dialog used from a tool page, pre-filled from
   * the selected Rule - see CreateRuleDialogComponent's edit mode. Only the
   * Rule-level watch/output/schedule settings are editable here; the tool's
   * own analysis parameters and any drawn regions carry through unchanged.
   * Server-side ownership enforcement (only the owner may update a Rule)
   * mirrors activate/deactivate/delete below - surfaced via the same 403
   * toast on save rather than a client-side ownership check here.
   */
  editSelected(): void {
    const rule = this.selectedRule;
    if (!rule) return;
    const ref = this.dialogService.open(CreateRuleDialogComponent, {
      header: 'Edit Rule',
      width: '32rem',
      data: { existingRule: rule },
    });
    ref.onClose.pipe(take(1)).subscribe((updated: RuleInfo | null | undefined) => {
      if (updated) {
        this.refresh();
      }
    });
  }

  activateSelected(): void {
    const rule = this.selectedRule;
    if (!rule) return;
    this.ruleService.activate(rule.id).subscribe({
      next: () => {
        this.toast('success', 'Rule Activated', `Rule #${rule.id} activated.`);
        this.refresh();
      },
      error: (err) => this.handleActionError(err, 'activate'),
    });
  }

  deactivateSelected(): void {
    const rule = this.selectedRule;
    if (!rule) return;
    this.ruleService.deactivate(rule.id).subscribe({
      next: () => {
        this.toast('success', 'Rule Deactivated', `Rule #${rule.id} deactivated.`);
        this.refresh();
      },
      error: (err) => this.handleActionError(err, 'deactivate'),
    });
  }

  confirmDeleteSelected(): void {
    const rule = this.selectedRule;
    if (!rule) return;
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the rule watching "${this.monitoredDirectory(rule)}"?`,
      header: 'Delete Rule',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.ruleService.delete(rule.id).subscribe({
          next: () => {
            this.toast('success', 'Rule Deleted', `Rule #${rule.id} deleted.`);
            this.selectedRule = null;
            this.refresh();
          },
          error: (err) => this.handleActionError(err, 'delete'),
        });
      },
    });
  }

  private handleActionError(err: any, action: string): void {
    const detail = err?.status === 403
      ? 'You do not have permission to modify this rule.'
      : `Could not ${action} the rule.`;
    this.toast('error', 'Action Failed', detail);
  }

  private toast(severity: string, summary: string, detail: string): void {
    this.messageService.add({ key: 'app-toast', severity, summary, detail });
  }
}
